/**
 * Peças do painel "Rastreio e previsão", comuns às abas das nuvens e da chuva:
 * a amostra do traço, o interruptor principal, o aviso, os números do quadro
 * e as próprias abas.
 */
import clsx from 'clsx';
import { useId, useRef, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { comAlfa, TRACEJADO, type Traco as TracoDoMapa } from './tracos';

/**
 * A amostra de um traço, na legenda e nos controles: o MESMO desenho do mapa
 * (`tracos.ts`) — casco contínuo por baixo, linha por cima, nas mesmas
 * espessuras e opacidades. `apagado`: a opção está desligada, a linha fica cinza.
 */
export function Traco(props: { traco: TracoDoMapa; apagado?: boolean; curto?: boolean }) {
  const t = props.traco;
  // `curto`: marcador discreto (a linha secundária dos núcleos); a espessura é a do mapa nos dois.
  const fim = props.curto ? 15 : 21;
  return (
    <svg width={fim + 1} height="10" viewBox={`0 0 ${fim + 1} 10`} aria-hidden className="shrink-0" data-traco-largura={t.largura} data-traco-casco={t.larguraCasco}>
      <line x1="1" y1="5" x2={fim} y2="5" stroke={t.casco} strokeWidth={t.larguraCasco} />
      <line
        x1="1"
        y1="5"
        x2={fim}
        y2="5"
        stroke={props.apagado ? '#7d8fae' : comAlfa(t.cor, t.alfa)}
        strokeWidth={t.largura}
        strokeDasharray={t.tracejado ? TRACEJADO.join(' ') : undefined}
      />
    </svg>
  );
}

/**
 * Controle PRINCIPAL — Rastreio e Previsão das nuvens, e o Rastreio da chuva:
 * a linha inteira é o alvo, com o traço da legenda, o nome, a descrição e o
 * estado dito de três jeitos — a palavra ("Ligado"/"Desligado"), o
 * interruptor desenhado e o fundo. O `Switch` dos primitivos não serve aqui:
 * é um botão, e não cabe dentro de outro.
 */
export function Interruptor(props: { nome: string; descricao: string; ligado: boolean; traco: TracoDoMapa; onChange: (on: boolean) => void }) {
  const id = useId();
  const { ligado } = props;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligado}
      aria-label={props.nome}
      aria-describedby={id}
      onClick={() => props.onChange(!ligado)}
      className={clsx(
        'flex h-full min-h-14 w-full items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mata-400',
        ligado ? 'border-mata-400 bg-mata-500/20' : 'border-ink-600 bg-ink-800 hover:border-ink-500',
      )}
    >
      {/* Num fundo escuro: o traço verde da chuva sumia sobre o verde do ligado. */}
      <span className="grid h-6 w-7 shrink-0 place-items-center rounded-md bg-ink-900/70">
        <Traco traco={props.traco} apagado={!ligado} />
      </span>
      <span className="min-w-0 flex-1">
        <span className={clsx('block text-sm font-semibold', ligado ? 'text-white' : 'text-ink-200')}>{props.nome}</span>
        <span id={id} className="mt-0.5 block text-[11px] leading-snug text-ink-300">
          {props.descricao}
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-center gap-1" aria-hidden>
        <span className={clsx('relative inline-flex h-5 w-9 items-center rounded-full transition-colors', ligado ? 'bg-mata-500' : 'bg-ink-600')}>
          <span className={clsx('inline-block size-4 rounded-full bg-white shadow transition-transform', ligado ? 'translate-x-4.5' : 'translate-x-0.5')} />
        </span>
        <span className={clsx('text-[11px] leading-none font-medium', ligado ? 'text-mata-300' : 'text-ink-400')}>{ligado ? 'Ligado' : 'Desligado'}</span>
      </span>
    </button>
  );
}

export function Aviso(props: { children: ReactNode }) {
  return <div className="rounded-lg border border-yellow-400/40 bg-yellow-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-yellow-200">{props.children}</div>;
}

export interface Numero {
  rotulo: string;
  valor: ReactNode;
  /** Unidade, menor, depois do valor. */
  unidade?: string;
}

/**
 * Os números do quadro na tela, em três quadros lado a lado — o que antes era
 * uma frase ("13 sistemas, 753.557 km² no total; topo mais frio a 195 K."). Em
 * cima, de que horário eles são. A frase inteira vai para o leitor de tela.
 */
export function Numeros(props: { titulo: ReactNode; numeros: Numero[]; frase: string; cor: string }) {
  return (
    <div role="status" aria-label={props.frase} data-numeros-do-quadro="" className="space-y-1">
      <div className="flex min-w-0 items-center gap-1.5 text-[11px] leading-none whitespace-nowrap text-ink-300 tabular-nums" aria-hidden>
        <span className="size-1.5 shrink-0 rounded-full" style={{ background: props.cor }} />
        {props.titulo}
      </div>
      <dl className="grid grid-cols-3 gap-1.5" aria-hidden>
        {props.numeros.map((n) => (
          <div key={n.rotulo} className="min-w-0 rounded-lg border border-ink-700/70 bg-ink-800/60 px-2.5 py-1.5">
            <dt className="truncate text-[11px] leading-tight text-ink-300">{n.rotulo}</dt>
            <dd className="mt-0.5 truncate leading-tight font-semibold text-white tabular-nums">
              <span className="text-[15px]">{n.valor}</span>
              {n.unidade && <span className="ml-0.5 text-[11px] font-normal text-ink-300">{n.unidade}</span>}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export interface Aba<K extends string> {
  chave: K;
  nome: string;
  /** Fonte e cadência: a dica do botão (a linha dos números do quadro também a diz). */
  fonte: string;
  icone: LucideIcon;
  traco: TracoDoMapa;
  /** Está desenhado no mapa agora. */
  noMapa: boolean;
}

/**
 * As abas do painel, na linha do cabeçalho (no lugar do título: no celular, a
 * folha não tem altura a perder): qual rastreio se está vendo. Cada uma diz
 * se o rastreio dela está no mapa — os dois podem estar —, e a escolhida tem a
 * borda e a faixa na cor do contorno que ele desenha. Teclado: uma parada de
 * Tab, e as setas trocam a aba (o padrão das abas da WAI-ARIA).
 */
export function Abas<K extends string>(props: { abas: Aba<K>[]; atual: K; aoEscolher: (k: K) => void; idDoPainel: (k: K) => string; idDaAba: (k: K) => string }) {
  const grupo = useRef<HTMLDivElement>(null);
  const comSetas = (e: React.KeyboardEvent) => {
    const i = props.abas.findIndex((a) => a.chave === props.atual);
    let alvo: number | null = null;
    if (e.key === 'ArrowRight') alvo = (i + 1) % props.abas.length;
    else if (e.key === 'ArrowLeft') alvo = (i - 1 + props.abas.length) % props.abas.length;
    else if (e.key === 'Home') alvo = 0;
    else if (e.key === 'End') alvo = props.abas.length - 1;
    if (alvo === null) return;
    e.preventDefault();
    const k = props.abas[alvo]!.chave;
    props.aoEscolher(k);
    grupo.current?.querySelector<HTMLElement>(`#${CSS.escape(props.idDaAba(k))}`)?.focus();
  };
  return (
    <div ref={grupo} role="tablist" aria-label="Qual rastreio" onKeyDown={comSetas} className="grid grid-cols-2 gap-1.5">
      {props.abas.map((a) => {
        const sel = a.chave === props.atual;
        const Icone = a.icone;
        return (
          <button
            key={a.chave}
            id={props.idDaAba(a.chave)}
            type="button"
            role="tab"
            aria-selected={sel}
            aria-controls={props.idDoPainel(a.chave)}
            tabIndex={sel ? 0 : -1}
            title={a.fonte}
            onClick={() => props.aoEscolher(a.chave)}
            data-aba-rastreio={a.chave}
            className={clsx(
              'relative flex min-h-11 min-w-0 flex-col justify-center gap-0.5 overflow-hidden rounded-lg border px-2.5 py-1 text-left transition-colors',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mata-400',
              // A escolhida se destaca pelo fundo e pela borda na cor do contorno; a outra fica rente ao painel.
              sel ? 'bg-ink-700 shadow-sm' : 'border-ink-700/80 bg-transparent hover:border-ink-600 hover:bg-ink-850',
            )}
            style={sel ? { borderColor: comAlfa(a.traco.cor, 0.7) } : undefined}
          >
            {/* A faixa na cor do contorno no mapa: amarelo, as nuvens; verde, a chuva. */}
            <span
              aria-hidden
              className={clsx('absolute inset-x-0 top-0 h-[2px] transition-opacity', sel ? 'opacity-100' : 'opacity-25')}
              style={{ background: comAlfa(a.traco.cor, 0.95) }}
            />
            <span className="flex items-center gap-1.5">
              <Icone className={clsx('size-4 shrink-0', sel ? 'text-white' : 'text-ink-300')} aria-hidden />
              <span className={clsx('truncate text-sm leading-tight font-semibold', sel ? 'text-white' : 'text-ink-200')}>{a.nome}</span>
            </span>
            <span className={clsx('flex items-center gap-1 text-[11px] leading-tight font-medium', a.noMapa ? 'text-mata-300' : 'text-ink-400')}>
              <span className={clsx('size-1.5 shrink-0 rounded-full', a.noMapa ? 'bg-mata-400' : 'bg-ink-500')} aria-hidden />
              {a.noMapa ? 'No mapa' : 'Desligado'}
            </span>
          </button>
        );
      })}
    </div>
  );
}
